import { Injectable } from '@angular/core';
import { PromoApi } from '@kp/shared/components/promo/promo.api';
import { Promo } from '@kp/shared/components/promo/promo.model';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PromoBloc {
  constructor(private _promoApi: PromoApi) {}

  private _promosSubject$ = new BehaviorSubject<Promo[]>(null);
  private _featuredPromoHadOpportunity = false;
  // Monotonic id used to drop responses from superseded getPromos() calls so a
  // late-arriving stale response cannot overwrite a fresher one in the
  // BehaviorSubject (e.g. across the paywall purchase boundary).
  private _promosRequestId = 0;

  get promosSubject$(): Observable<Promo[]> {
    return this._promosSubject$.asObservable();
  }

  getPromos(): void {
    const requestId = ++this._promosRequestId;
    this._promoApi
      .getPromos()
      .then((promos) => {
        if (requestId !== this._promosRequestId) {
          // A newer getPromos()/reset() superseded this request; drop the response.
          return;
        }
        // continue to mark all featured promos as shownAsFeatured if there's already been
        // an opportunity to display as a modal per _featuredPromoHadOpportunity until app is refreshed
        const normalizedPromos = this._featuredPromoHadOpportunity
          ? promos.map((promo) => {
              if (promo?.featuredForToday) {
                promo.shownAsFeatured = true;
              }
              return promo;
            })
          : promos;
        this._promosSubject$.next(normalizedPromos);
      })
      .catch((error) => {
        if (requestId !== this._promosRequestId) return;
        console.warn('There was an error getting promos: ', error);
        this._promosSubject$.next(null);
      });
  }

  /**
   * Discards any cached promo list and invalidates any in-flight getPromos()
   * response. Used when entering a flow (e.g. the paywall) where the cached
   * value is about to become stale and we don't want consumers (notably the
   * featured-promo modal) to act on the pre-flow data.
   */
  reset(): void {
    this._promosRequestId++;
    this._featuredPromoHadOpportunity = false;
    this._promosSubject$.next(null);
  }

  removePromo(promoId: string) {
    this._promosSubject$.next(this._promosSubject$.getValue()?.filter((promo) => promo.id !== promoId));
  }

  allowFeaturePromoModal() {
    this._featuredPromoHadOpportunity = false;
  }

  preventFeaturePromoModal() {
    this._featuredPromoHadOpportunity = true;
  }
}
